import { ConversationKind, type ApiConversation } from '@/api/conversations/types/index.ts';
import Model from './Model.js';
import ConversationLead from './ConversationLead.ts';
import ConversationLeadPhone from './ConversationLeadPhone.ts';
import ConversationContactPhone from './ConversationContactPhone.ts';
import ConversationUser from './ConversationUser.ts';
import ConversationSmsProvider from './ConversationSmsProvider.ts';
import type { Message } from './types/ConversationType.ts';

export default class Conversation extends Model {
    declare id: number;

    declare kind: ConversationKind;

    declare readOnly: boolean;

    declare lead: ConversationLead;

    declare leadPhone: ConversationLeadPhone | null;

    declare contactPhone: ConversationContactPhone | null;

    declare smsProvider: ConversationSmsProvider | null;

    declare latestMessageId: number | null;

    declare time: string;

    declare lastMessage: string;

    declare archived: boolean;

    declare messages: Message[];

    declare conversationUsers: ConversationUser[];

    declare currentUserId: number | null;

    static get defaults() {
        return {
            id: 0,
            kind: ConversationKind.NATIVE,
            readOnly: false,
            lead: new ConversationLead(),
            leadPhone: new ConversationLeadPhone(),
            contactPhone: null,
            smsProvider: new ConversationSmsProvider(),
            latestMessageId: null,
            time: '',
            lastMessage: '',
            archived: false,
            messages: [],
            conversationUsers: [],
            currentUserId: null,
        };
    }

    static fromApi(
        conversation: ApiConversation,
        overrides: Partial<Conversation> = {},
        currentUserId: number | null = null
    ): Conversation {
        const lead = ConversationLead.fromApi(conversation.lead);
        const leadPhone = conversation.leadPhone
            ? ConversationLeadPhone.fromApi(conversation.leadPhone)
            : null;
        const contactPhone = conversation.contactPhone
            ? ConversationContactPhone.fromApi(conversation.contactPhone)
            : null;
        const smsProvider = conversation.smsProvider
            ? ConversationSmsProvider.fromApi(conversation.smsProvider)
            : null;
        const conversationUsers = conversation.conversationUsers.map(ConversationUser.fromApi);

        return new Conversation({
            id: conversation.id,
            kind: conversation.kind,
            readOnly: conversation.readOnly,
            lead,
            leadPhone,
            contactPhone,
            smsProvider,
            latestMessageId: conversation.latestMessage?.id ?? null,
            time:
                conversation.latestMessage?.original_created_at ||
                conversation.original_created_at ||
                '',
            lastMessage: conversation.latestMessage?.preview || '',
            archived: conversation.archived,
            messages: [],
            currentUserId,
            conversationUsers,
            ...overrides,
        });
    }

    constructor(initialValues: Partial<Conversation> = {}) {
        super(initialValues);
    }

    get leadName(): string {
        return this.lead?.fullName || '';
    }

    get isGroup(): boolean {
        return this.conversationUsers.length > 1;
    }

    get identityKey(): string {
        return `${this.kind}:${this.id}`;
    }

    get isLegacy(): boolean {
        return this.kind === ConversationKind.LEGACY;
    }

    get conversationUserIds(): number[] {
        return this.conversationUsers.map((conversationUser) => conversationUser.userId);
    }

    get isUnread(): boolean {
        if (this.isLegacy) {
            return false;
        }

        if (!this.currentUserId) {
            return false;
        }

        const currentUserEntry = this.conversationUsers.find(
            (conversationUser) => conversationUser.userId === this.currentUserId
        );

        if (!currentUserEntry) {
            return false;
        }

        return !currentUserEntry.allMessagesReadAt;
    }
}
